import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  FileText,
  Sparkles,
  Mail,
  Info,
} from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { SiteFooter } from "@/components/SiteFooter";
import { StepProgress } from "@/components/StepProgress";
import { IDS } from "@/lib/testIds";
import { submitDiscovery } from "@/lib/api";

const ACK_TEXT =
  "I understand that this is a manual invited discovery, that the KATLAS team will review the submitted public links, and that OpenAI may assist with preparing the exploratory report.";

const HOW_STEPS = [
  "You share your LinkedIn profile, website and report email address.",
  "The KATLAS team reviews the public information and manually runs a bounded mapping process.",
  "OpenAI may assist with structuring the exploratory mapping.",
  "The KATLAS team reviews the result and KATLAS sends the exploratory report to you by email.",
  "Your feedback helps improve the future automated service and any proposed prototype.",
];

function makeIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---- Small presentational helpers -----------------------------------------

function Eyebrow() {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-deep">
      KATLAS Cierge Design Studio
    </span>
  );
}

function Field({ id, label, help, placeholder, type, value, onChange, error, errId }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-semibold text-ink">
        {label}
      </label>
      <p className="text-[13px] leading-snug text-muted">{help}</p>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? errId : undefined}
        className={`mt-1 w-full rounded-lg border bg-white px-3.5 py-3 font-mono text-[14px] text-ink transition-colors placeholder:text-muted/50 focus:border-teal ${
          error ? "border-red-400" : "border-muted-line"
        }`}
      />
      {error && (
        <p id={errId} data-testid={errId} className="text-[13px] font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ---- Confirmation ----------------------------------------------------------

function Confirmation({ result }) {
  return (
    <div className="min-h-screen bg-stone-base paper-grain">
      <div className="mx-auto max-w-2xl px-6 pt-10">
        <BrandMark />
      </div>
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <StepProgress current={3} />
      </div>
      <motion.main
        data-testid={IDS.confirmationRoot}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-2xl px-6 pb-14 pt-8"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-soft text-teal-deep">
          <Check className="h-6 w-6" strokeWidth={2.2} />
        </span>
        <h1 className="mt-6 font-serif text-[34px] leading-tight text-ink sm:text-[40px]">
          Your discovery details have been received
        </h1>
        <p className="mt-5 text-[16px] leading-relaxed text-ink-soft">
          The KATLAS team will review the public information supplied and prepare
          the initial exploratory report for the email address provided:
        </p>
        <p
          data-testid={IDS.confirmationEmail}
          className="mt-3 font-mono text-[15px] font-medium text-teal-deep"
        >
          {result.report_email}
        </p>

        <div className="mt-8 rounded-xl border border-muted-line bg-stone-soft p-6">
          <p className="text-[14px] font-semibold text-ink">The report will consider:</p>
          <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-ink-soft">
            {[
              "your current proposition and customer journey;",
              "where trust, authority or evidence may matter as the estate grows;",
              "which existing infrastructure should remain in place; and",
              "one bounded opportunity for further testing or prototyping.",
            ].map((t) => (
              <li key={t} className="flex gap-2.5">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-teal" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-line bg-amber-soft px-5 py-4">
          <Info className="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber-deep" strokeWidth={2} />
          <p className="text-[13.5px] font-medium leading-relaxed text-amber-deep">
            Manual report preparation — not an automated assessment.
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          <a
            href="https://katlastechnology.com"
            data-testid={IDS.returnButton}
            className="inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3 text-[15px] font-medium text-stone-soft transition-colors hover:bg-ink-deep"
          >
            Return to KATLAS
            <ArrowRight className="h-4 w-4" />
          </a>
          <p
            data-testid={IDS.confirmationRef}
            className="text-[12px] leading-relaxed text-muted"
          >
            Application-generated invited-test record — not a KATLAS-node-signed
            operational receipt.
            <br />
            <span className="font-mono">
              {result.reference} · {result.submitted_readable_uk}
            </span>
          </p>
        </div>
      </motion.main>
      <SiteFooter />
    </div>
  );
}

// ---- Main intake (opens directly at /start) --------------------------------

export default function InvitedIntake() {
  const [result, setResult] = useState(null);

  const [linkedin, setLinkedin] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const idemKey = useRef(makeIdempotencyKey());

  const validate = () => {
    const e = {};
    const ln = linkedin.trim();
    const ws = website.trim();
    const em = email.trim();
    const urlRe = /^https?:\/\/.+/i;
    if (!ln) e.linkedin = "The LinkedIn profile URL is required.";
    else if (!urlRe.test(ln)) e.linkedin = "Enter a valid http or https URL.";
    else if (!/^https?:\/\/(www\.)?([a-z0-9-]+\.)*linkedin\.com\//i.test(ln))
      e.linkedin = "This must be a public linkedin.com profile URL.";
    if (!ws) e.website = "The website URL is required.";
    else if (!urlRe.test(ws)) e.website = "Enter a valid http or https URL.";
    if (!em) e.email = "The report email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em))
      e.email = "Enter a valid email address.";
    if (!ack) e.ack = "Please confirm the process acknowledgement to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = await submitDiscovery({
        linkedin_url: linkedin.trim(),
        website_url: website.trim(),
        report_email: email.trim(),
        acknowledged: ack,
        idempotency_key: idemKey.current,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string"
          ? detail
          : "We could not submit your request. Please check the fields and try again."
      );
      setSubmitting(false);
    }
  };

  if (result) return <Confirmation result={result} />;

  return (
    <div data-testid={IDS.intakeRoot} className="min-h-screen bg-stone-base paper-grain">
      {/* Status banner */}
      <div
        data-testid={IDS.statusBanner}
        className="border-b border-amber-line bg-amber-soft"
      >
        <div className="mx-auto flex max-w-3xl flex-col gap-0.5 px-6 py-2.5 text-center sm:flex-row sm:items-center sm:justify-center sm:gap-3 sm:text-left">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-deep">
            Invited Discovery Test
          </span>
          <span className="text-[12.5px] text-amber-deep/90">
            An early testing environment for invited participants. No payment is
            required and no account or password is needed.
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pt-10">
        <BrandMark />
      </div>

      <div className="mx-auto max-w-3xl px-6 pt-8">
        <StepProgress current={2} />
      </div>

      <main className="mx-auto max-w-3xl px-6 pb-16 pt-8">
        <Link
          to="/"
          data-testid={IDS.backToOverview}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted transition-colors hover:text-teal"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Review how the discovery works
        </Link>

        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7"
        >
          <Eyebrow />
          <h1
            data-testid={IDS.heroHeading}
            className="mt-3 font-serif text-[38px] leading-[1.1] text-ink sm:text-[48px]"
          >
            Start your first bounded discovery
          </h1>
          <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-ink-soft">
            You have been invited to help us test a simple KATLAS Cierge Design
            Studio discovery process.
          </p>
          <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-ink-soft">
            Share three public details and the KATLAS team will manually prepare
            an initial exploratory report. The report will help us understand
            your current proposition, where trust and control may matter as you
            scale, which existing infrastructure should remain in place, and one
            bounded opportunity that may be suitable for prototyping.
          </p>
        </motion.header>

        {/* How it works */}
        <section data-testid={IDS.howItWorks} className="mt-12">
          <h2 className="font-serif text-[22px] text-ink">How this first test works</h2>
          <ol className="mt-5 space-y-3">
            {HOW_STEPS.map((step, i) => (
              <li
                key={i}
                className="flex gap-4 rounded-lg border border-muted-line bg-stone-soft px-4 py-3.5"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink font-mono text-[12px] text-stone-soft">
                  {i + 1}
                </span>
                <span className="text-[14.5px] leading-relaxed text-ink-soft">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Callouts */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div
            data-testid={IDS.manualCallout}
            className="rounded-xl border border-teal/25 bg-teal-soft p-5"
          >
            <div className="flex items-center gap-2 text-teal-deep">
              <Sparkles className="h-4 w-4" strokeWidth={2} />
              <h3 className="font-serif text-[17px]">Manual first, automation later</h3>
            </div>
            <p className="mt-2.5 text-[13.5px] leading-relaxed text-ink-soft">
              This first walkthrough is intentionally manual. We are testing the
              explanation, questions, report and data-handling approach before
              automating the commercial service. A future control envelope may
              make purpose, provider routing, data boundaries, retention and
              release conditions executable before processing proceeds.
            </p>
          </div>
          <div
            data-testid={IDS.feeCallout}
            className="rounded-xl border border-amber-line bg-amber-soft p-5"
          >
            <div className="flex items-center gap-2 text-amber-deep">
              <FileText className="h-4 w-4" strokeWidth={2} />
              <h3 className="font-serif text-[17px]">Invited trial fee: £0</h3>
            </div>
            <p className="mt-2.5 text-[13.5px] leading-relaxed text-amber-deep/90">
              No payment details will be collected. The future paid service and
              commercial terms are not active in this first walkthrough.
            </p>
          </div>
        </div>

        {/* Form */}
        <section className="mt-12">
          <h2 className="font-serif text-[24px] leading-snug text-ink">
            Provide the three details needed for your first report
          </h2>
          <form
            data-testid={IDS.form}
            onSubmit={onSubmit}
            noValidate
            className="mt-6 rounded-2xl border border-muted-line bg-white/70 p-6 sm:p-8"
          >
            <div className="space-y-6">
              <Field
                id={IDS.linkedinInput}
                label="LinkedIn profile URL"
                help="The public LinkedIn profile you would like us to use for this exploration."
                placeholder="https://www.linkedin.com/in/..."
                type="url"
                value={linkedin}
                onChange={setLinkedin}
                error={errors.linkedin}
                errId={IDS.linkedinError}
              />
              <Field
                id={IDS.websiteInput}
                label="Website URL"
                help="The public website that best represents your current proposition."
                placeholder="https://example.com"
                type="url"
                value={website}
                onChange={setWebsite}
                error={errors.website}
                errId={IDS.websiteError}
              />
              <Field
                id={IDS.emailInput}
                label="Email address for the report"
                help="We will use this address to deliver the report and communicate about this invited test. Do not include this email address in the AI mapping prompt."
                placeholder="name@organisation.com"
                type="email"
                value={email}
                onChange={setEmail}
                error={errors.email}
                errId={IDS.emailError}
              />
            </div>

            {/* Data & AI notice */}
            <div className="mt-8 rounded-xl border border-muted-line bg-stone-soft p-5">
              <h3 className="font-serif text-[18px] text-ink">
                How the information will be used
              </h3>
              <div className="mt-3 space-y-3 text-[13px] leading-relaxed text-muted">
                <p>
                  The KATLAS team will see the three details you submit and may
                  review the public pages they identify. Relevant public-source
                  information may be processed using OpenAI to assist with the
                  exploratory mapping. Your report email address is used to
                  deliver the report and manage this invited test; it must not be
                  included in the AI mapping prompt.
                </p>
                <p>
                  Please submit only public links and an email address you are
                  authorised to provide. Do not submit customer records, facial
                  scans, passwords, payment information, confidential contracts,
                  trade secrets, legally privileged material or other sensitive
                  information.
                </p>
                <p>
                  The submitted details, working analysis and report may be
                  retained for up to 30 days after the report and feedback
                  discussion, then deleted from the active trial workspace unless
                  the KATLAS team and the participant agree to continue into a
                  prototype stage.
                </p>
                <p>
                  The report is exploratory, may contain errors and is not
                  professional legal, regulatory, financial, medical, security or
                  compliance advice.
                </p>
              </div>
            </div>

            {/* Acknowledgement */}
            <div className="mt-6">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  data-testid={IDS.ackCheckbox}
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  aria-invalid={!!errors.ack}
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-muted-line accent-teal"
                />
                <span className="text-[13.5px] leading-relaxed text-ink-soft">
                  {ACK_TEXT}
                </span>
              </label>
              {errors.ack && (
                <p
                  data-testid={IDS.ackError}
                  className="mt-2 text-[13px] font-medium text-red-600"
                >
                  {errors.ack}
                </p>
              )}
            </div>

            {serverError && (
              <p className="mt-6 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13.5px] text-red-700">
                {serverError}
              </p>
            )}

            {/* Submit */}
            <div className="mt-8">
              <button
                type="submit"
                data-testid={IDS.submitButton}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-teal-deep disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Start my invited discovery
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="mt-3 flex items-center gap-1.5 text-[13px] text-muted">
                <Mail className="h-3.5 w-3.5" />
                No account, password or payment details are required.
              </p>
            </div>
          </form>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
